import React, { useState } from 'react';
import { Button, Dialog, Heading, IconButton, Input, Select, ButtonArea, Text } from '../../components/base';
import { Textarea as BaseTextarea } from '../../components/base/Textarea';
import { AgentInputFormItem, AgentTextInputField, AgentSelectField, getAgentInputField } from '../../libs/models/app/AgentInput';

interface AgentInputFormProps {
  agentName?: string;
  userInputForm: AgentInputFormItem[];
  onSubmit: (inputs: Record<string, string>) => void;
  onCancel: () => void;
  initialValues?: Record<string, string>;
}

const AgentInputForm: React.FC<AgentInputFormProps> = ({ userInputForm, onSubmit, onCancel, initialValues }) => {
  const [inputs, setInputs] = useState<Record<string, string>>(() => {
    if (initialValues && Object.keys(initialValues).length > 0) return initialValues;
    const defaults: Record<string, string> = {};
    for (const item of userInputForm) {
      const result = getAgentInputField(item);
      if (result) {
        defaults[result.field.variable] = result.field.default_var ?? '';
      }
    }
    return defaults;
  });

  const handleChange = (variable: string, value: string) => {
    setInputs((prev) => ({ ...prev, [variable]: value }));
  };

  const hasEmpty = userInputForm.some((fi) => {
    const r = getAgentInputField(fi);
    return r && r.field.required && !inputs[r.field.variable]?.trim();
  });

  const handleSubmit = () => {
    if (hasEmpty) return;
    onSubmit(inputs);
  };

  const renderField = (item: AgentInputFormItem, index: number) => {
    const result = getAgentInputField(item);
    if (!result) return null;

    const { type, field } = result;

    const label = <label className={`dsx-Label${field.required ? ' is-required' : ''}`}>{field.label}</label>;

    switch (type) {
      case 'text-input':
        return (
          <div key={index} className="dsx-FormField">
            {label}
            <Input
              full
              size="large"
              value={inputs[field.variable] ?? ''}
              maxLength={(field as AgentTextInputField).max_length}
              placeholder={field.default_var ?? `${field.label}을(를) 입력하세요`}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                handleChange(field.variable, e.target.value);
              }}
            />
          </div>
        );

      case 'select':
        return (
          <div key={index} className="dsx-FormField">
            {label}
            <Select
              full
              size="large"
              value={inputs[field.variable] ?? ''}
              options={(field as AgentSelectField).options.map((opt) => ({ value: opt, label: opt }))}
              onChange={(value: string | number) => {
                handleChange(field.variable, String(value));
              }}
            />
          </div>
        );

      case 'paragraph':
        return (
          <div key={index} className="dsx-FormField">
            {label}
            <BaseTextarea
              value={inputs[field.variable] ?? ''}
              placeholder={field.default_var ?? `${field.label}을(를) 입력하세요`}
              rows={3}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                handleChange(field.variable, e.target.value);
              }}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog isOpen={true} onClose={onCancel} className="agent-input-dialog">
      <div className="agent-input-body">
        <div className="agent-input-head">
          <Heading as="h2" size="title3" weight="semibold">
            변수 입력
          </Heading>
          <div className="agent-input-utils">
            <IconButton name="closeLarge" className="dsx-Dialog-close" onClick={onCancel}>
              닫기
            </IconButton>
          </div>
        </div>
        {hasEmpty && <Text variant="error" size="body2" className="agent-input-error">필수 입력값을 입력해주세요.</Text>}

        <div className="agent-input-fields">
          {userInputForm.map(renderField)}
        </div>

        <ButtonArea align="full">
          <Button size="large" variant="outline" onClick={onCancel}>
            취소
          </Button>
          <Button size="large" variant="primary" onClick={handleSubmit}>
            대화 시작
          </Button>
        </ButtonArea>
      </div>
    </Dialog>
  );
};

export default AgentInputForm;
